import { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue } from 'framer-motion';
import { X } from 'lucide-react';
import { useTenant } from '@/hooks/useTenant';
import { useTodosShared } from '@/context/TodosContext';
import { useTodoWidget } from '@/store/todoWidget';
import { TODO_COLORS, TODO_COLOR_ORDER, type TodoColorKey } from '@/lib/todoColors';
import { getNextColor, CategoryTabs } from '@/components/todos/CategoryTabs';
import { PostitIcon } from '@/components/todos/PostitIcon';
import { PostitNote } from '@/components/todos/PostitNote';

const MIN_W = 220;
const MIN_H = 200;

export function TodoFab() {
  const { tenantId } = useTenant();
  const todos        = useTodosShared();
  const store        = useTodoWidget();

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const dragMoved  = useRef(false);
  const resizeStart = useRef<{ x: number; y: number; w: number; h: number } | null>(null);

  const [hovered, setHovered]         = useState(false);
  const [isAddingCat, setIsAddingCat] = useState(false);
  const [newCatName, setNewCatName]   = useState('');
  const [newCatColor, setNewCatColor] = useState<TodoColorKey>('yellow');

  // Inicializa posição e tamanho no canto inferior direito se nunca foram salvos
  useEffect(() => {
    const w = store.widgetWidth  || 264;
    const h = store.widgetHeight || 380;
    if (store.x === 0 && store.y === 0) {
      // Deslocado para cima para não sobrepor o chat flutuante (canto inferior direito)
      store.setPosition(
        Math.max(0, window.innerWidth  - w - 24),
        Math.max(0, window.innerHeight - h - 104),
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sincroniza categoria ativa
  const activeExists = todos.categories.some((c) => c.id === store.activeCategoryId);
  const activeCategory =
    (activeExists ? todos.categories.find((c) => c.id === store.activeCategoryId) : null)
    ?? todos.categories[0]
    ?? null;

  useEffect(() => {
    if (activeCategory && store.activeCategoryId !== activeCategory.id) {
      store.setActiveCategory(activeCategory.id);
    }
  }, [activeCategory, store]);

  // Só some durante o carregamento INICIAL (loading sem nada carregado ainda).
  // Recargas posteriores não desmontam o widget (evita o "piscar" ao criar
  // categoria/tarefa) e com zero categorias ele continua visível — o estado
  // vazio já oferece o "+" para criar.
  if (!tenantId || store.dismissed) return null;
  if (todos.loading && todos.categories.length === 0) return null;

  // ─── Resize handle ────────────────────────────────────────────────────────

  function startResize(e: React.PointerEvent) {
    e.stopPropagation();
    e.preventDefault();
    resizeStart.current = {
      x: e.clientX, y: e.clientY,
      w: store.widgetWidth  || 264,
      h: store.widgetHeight || 380,
    };

    const onMove = (ev: PointerEvent) => {
      if (!resizeStart.current) return;
      store.setWidgetSize(
        Math.max(MIN_W, resizeStart.current.w + (ev.clientX - resizeStart.current.x)),
        Math.max(MIN_H, resizeStart.current.h + (ev.clientY - resizeStart.current.y)),
      );
    };
    const onUp = () => {
      resizeStart.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup',   onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup',   onUp);
  }

  // ─── Nova categoria ───────────────────────────────────────────────────────

  function openAddCat() {
    setNewCatColor(getNextColor(todos.categories));
    setNewCatName('');
    setIsAddingCat(true);
  }

  async function commitAddCat() {
    if (!newCatName.trim()) { setIsAddingCat(false); return; }
    await todos.addCategory(newCatName.trim(), newCatColor).catch(console.error);
    setIsAddingCat(false);
    setNewCatName('');
  }

  function cancelAddCat() { setIsAddingCat(false); setNewCatName(''); }

  // ─── Handlers categorias existentes ──────────────────────────────────────

  function handleDeleteCategory(id: string, hasTasks: boolean) {
    if (hasTasks && !window.confirm('Esta categoria tem tarefas. Deseja excluí-la mesmo assim?')) return;
    void todos.deleteCategory(id).catch(console.error);
  }

  // ─── Posição e tamanho ────────────────────────────────────────────────────

  const W = store.widgetWidth  || 264;
  const H = store.widgetHeight || 380;
  const posX = store.x || Math.max(0, window.innerWidth  - W - 24);
  const posY = store.y || Math.max(0, window.innerHeight - H - 24);
  // Altura disponível para lista de tarefas (subtrai header ~30px + footer ~50px + tabs ~28px + form ~80px se ativo)
  const formH         = isAddingCat ? 90 : 0;
  const taskAreaHeight = Math.max(80, H - 30 - 50 - 28 - formH);

  const formColors = TODO_COLORS[newCatColor];

  return (
    <motion.div
      drag
      dragMomentum={false}
      style={{ position: 'fixed', left: posX, top: posY, x: mx, y: my, zIndex: 50, cursor: store.collapsed ? 'pointer' : 'default' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDrag={(_, info) => {
        if (Math.abs(info.offset.x) > 4 || Math.abs(info.offset.y) > 4) dragMoved.current = true;
      }}
      onDragEnd={(_, info) => {
        if (dragMoved.current) {
          const cW = store.collapsed ? 50 : W;
          const cH = store.collapsed ? 54 : H;
          store.setPosition(
            Math.min(Math.max(0, posX + info.offset.x), window.innerWidth  - cW),
            Math.min(Math.max(0, posY + info.offset.y), window.innerHeight - cH),
          );
          mx.set(0);
          my.set(0);
        }
        setTimeout(() => { dragMoved.current = false; }, 30);
      }}
      onClick={() => { if (!dragMoved.current && store.collapsed) store.expand(); }}
    >
      {store.collapsed ? (
        /* ── Ícone minimizado ──────────────────────────────────────────────── */
        <div style={{ position: 'relative' }}>
          <PostitIcon pendingCount={todos.pendingCount} />
          {hovered && (
            <button
              onPointerDownCapture={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); store.dismiss(); }}
              title="Ocultar widget"
              style={{
                position: 'absolute', top: -6, right: -6,
                width: 18, height: 18, borderRadius: '50%',
                backgroundColor: '#374151', border: '1.5px solid #fff',
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', zIndex: 1,
              }}
            >
              <X size={10} strokeWidth={2.5} />
            </button>
          )}
        </div>
      ) : (
        /* ── Widget expandido ──────────────────────────────────────────────── */
        <div style={{ display: 'flex', flexDirection: 'column', userSelect: 'none', position: 'relative', width: W }}>

          {/* Abas de categorias — sempre visível para o + funcionar mesmo sem categorias */}
          <CategoryTabs
            categories={todos.categories}
            activeCategoryId={activeCategory?.id ?? null}
            onSelect={(id) => store.setActiveCategory(id)}
            onDelete={handleDeleteCategory}
            onRename={(id, name) => void todos.renameCategory(id, name).catch(console.error)}
            onAdd={openAddCat}
          />

          {/* Formulário de nova categoria */}
          {isAddingCat && (
            <div
              onPointerDownCapture={(e) => e.stopPropagation()}
              style={{
                backgroundColor: formColors.bg,
                padding: '8px 10px 10px',
                borderBottom: `1px solid ${formColors.edge}`,
              }}
            >
              <input
                autoFocus
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter')  void commitAddCat();
                  if (e.key === 'Escape') cancelAddCat();
                }}
                placeholder="Nome da categoria…"
                style={{
                  width: '100%', background: 'transparent',
                  border: 'none', borderBottom: `1px solid ${formColors.ink}40`,
                  outline: 'none', fontSize: 12, color: formColors.ink,
                  padding: '2px 0', marginBottom: 8, boxSizing: 'border-box',
                }}
              />
              {/* Seletor de cor */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                {TODO_COLOR_ORDER.map((key) => (
                  <button
                    key={key}
                    onClick={() => setNewCatColor(key)}
                    title={key}
                    style={{
                      width: 16, height: 16, borderRadius: '50%',
                      backgroundColor: TODO_COLORS[key].bg,
                      border: newCatColor === key
                        ? `2px solid ${TODO_COLORS[key].ink}`
                        : '2px solid rgba(0,0,0,.12)',
                      cursor: 'pointer',
                      transform: newCatColor === key ? 'scale(1.25)' : 'none',
                      transition: 'transform .12s',
                    }}
                  />
                ))}
              </div>
              {/* Botões */}
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => void commitAddCat()}
                  style={{
                    fontSize: 11, padding: '3px 10px', borderRadius: 4,
                    backgroundColor: formColors.ink, color: formColors.bg,
                    border: 'none', cursor: 'pointer', fontWeight: 600,
                  }}
                >
                  Criar
                </button>
                <button
                  onClick={cancelAddCat}
                  style={{
                    fontSize: 11, padding: '3px 8px', borderRadius: 4,
                    background: 'transparent', color: formColors.ink,
                    border: `1px solid ${formColors.ink}50`, cursor: 'pointer',
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Corpo do post-it */}
          {todos.loading ? (
            <div style={{ width: W, height: taskAreaHeight + 80, backgroundColor: '#FFE16B', borderRadius: '3px 3px 4px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#6b540080', boxShadow: '2px 4px 14px rgba(0,0,0,.22)' }}>
              Carregando…
            </div>
          ) : activeCategory ? (
            <PostitNote
              category={activeCategory}
              width={W}
              taskAreaHeight={taskAreaHeight}
              onMinimize={() => store.collapse()}
              onToggleTask={(id) => void todos.toggleTask(id).catch(console.error)}
              onAddTask={(content) => void todos.addTask(activeCategory.id, content).catch(console.error)}
              onEditTask={(id, content) => void todos.editTask(id, content).catch(console.error)}
            />
          ) : (
            <div style={{ width: W, height: taskAreaHeight + 80, backgroundColor: '#FFE16B', borderRadius: '3px 3px 4px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#6b5400', boxShadow: '2px 4px 14px rgba(0,0,0,.22)', textAlign: 'center', padding: 16 }}>
              Clique em <strong style={{ margin: '0 4px' }}>+</strong> para criar uma categoria.
            </div>
          )}

          {/* Alça de resize — canto inferior direito */}
          <div
            onPointerDownCapture={startResize}
            title="Redimensionar"
            style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 22, height: 22,
              cursor: 'nwse-resize',
              display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
              padding: 4, zIndex: 10,
            }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" style={{ opacity: 0.35 }}>
              <circle cx="8.5" cy="8.5" r="1.2" fill="currentColor" />
              <circle cx="5"   cy="8.5" r="1.2" fill="currentColor" />
              <circle cx="8.5" cy="5"   r="1.2" fill="currentColor" />
            </svg>
          </div>
        </div>
      )}
    </motion.div>
  );
}
